import { Box, Chip, Typography } from "@mui/material";
import CallMadeIcon from "@mui/icons-material/CallMade";
import CallReceivedIcon from "@mui/icons-material/CallReceived";
import SyncAltIcon from "@mui/icons-material/SyncAlt";
import { DataGridPro } from "@mui/x-data-grid-pro";
import { dataGridStyle, colors } from "../../../../theme";
import { useEffect, useState } from "react";
import API from "../../../../api";
import dayjs from "dayjs";
import utc from "dayjs/plugin/utc";
import timezone from "dayjs/plugin/timezone";

dayjs.extend(utc);
dayjs.extend(timezone);

const DIRECTION_STYLE = {
  Both:        { color: "#68BBBC", icon: SyncAltIcon,     label: "Bidirectional" },
  Source:      { color: "#CBBA54", icon: CallMadeIcon,    label: "Outbound" },
  Destination: { color: "#c14f60", icon: CallReceivedIcon, label: "Inbound" },
};

// Internal connections table for this asset's IP — a row is already the full
// conduit record (no security-log drill-down underneath it anymore).
const IpTable = ({ ipAddress, setGraphData, loading, setLoading, dateFilter }) => {
  const [paginationModel, setPaginationModel] = useState({ page: 0, pageSize: 50 });
  const [data, setData] = useState([]);
  const [rowCounter, setRowCounter] = useState(0);

  useEffect(() => {
    setLoading(true);
    const abortController = new AbortController();
    const { signal } = abortController;

    API.getConduits({
      ip: ipAddress,
      pageNumber: paginationModel.page,
      limit: paginationModel.pageSize,
      dateFrom: dateFilter?.dateFrom.utc().format("YYYY-MM-DD HH:mm"),
      dateTo: dateFilter?.dateTo.utc().format("YYYY-MM-DD HH:mm"),
    }, signal)
      .then((result) => {
        const transformedData = result.data.data.map((item, index) => ({
          id: index,
          ...item,
        }));
        setGraphData(result.data.data);
        setData(transformedData);
        setLoading(false);
        setRowCounter(result.data.totalLogs);
      })
      .catch((error) => {
        console.error("[IpTable] Error fetching connections:", error);
        setLoading(false);
      });

    return () => abortController.abort();
  }, [ipAddress, paginationModel.page, paginationModel.pageSize, dateFilter, setGraphData, setLoading]);

  const columns = [
    {
      field: "sourceIp",
      headerName: "SOURCE",
      flex: 1,
      minWidth: 140,
      headerAlign: "left",
      align: "left",
      renderCell: (params) => (
        <Typography sx={{ fontFamily: "monospace", fontSize: 12.5, color: params.value === ipAddress ? "#459095" : colors.grey[100], fontWeight: params.value === ipAddress ? 700 : 400 }}>
          {params.value}
        </Typography>
      ),
    },
    {
      field: "destIp",
      headerName: "DESTINATION",
      flex: 1,
      minWidth: 140,
      headerAlign: "left",
      align: "left",
      renderCell: (params) => (
        <Typography sx={{ fontFamily: "monospace", fontSize: 12.5, color: params.value === ipAddress ? "#459095" : colors.grey[100], fontWeight: params.value === ipAddress ? 700 : 400 }}>
          {params.value}
        </Typography>
      ),
    },
    {
      field: "type",
      headerName: "DIRECTION",
      flex: 0.8,
      minWidth: 150,
      headerAlign: "left",
      align: "left",
      renderCell: (params) => {
        const style = DIRECTION_STYLE[params.value] ?? { color: colors.grey[400], icon: SyncAltIcon, label: params.value };
        const Icon = style.icon;
        return (
          <Chip
            icon={<Icon sx={{ fontSize: "14px !important" }} />}
            label={style.label}
            size="small"
            sx={{
              bgcolor: `${style.color}22`, color: style.color, fontWeight: 600, fontSize: 11, height: 22,
              "& .MuiChip-icon": { color: style.color },
            }}
          />
        );
      },
    },
    {
      field: "sourceZone",
      headerName: "SOURCE ZONE",
      flex: 0.7,
      minWidth: 120,
      headerAlign: "left",
      align: "left",
      renderCell: (params) => (
        <Typography sx={{ fontSize: 12.5, color: colors.grey[100] }}>
          {params.value || "—"}
        </Typography>
      ),
    },
    {
      field: "destZone",
      headerName: "DEST ZONE",
      flex: 0.7,
      minWidth: 120,
      headerAlign: "left",
      align: "left",
      renderCell: (params) => (
        <Typography sx={{ fontSize: 12.5, color: colors.grey[100] }}>
          {params.value || "—"}
        </Typography>
      ),
    },
    {
      field: "allowed",
      headerName: "ALLOWED",
      flex: 0.6,
      minWidth: 110,
      headerAlign: "left",
      align: "left",
      renderCell: (params) => {
        const value = params.value;
        const style = value === true
          ? { color: "#68BBBC", label: "Allowed" }
          : value === false
            ? { color: "#c14f60", label: "Not allowed" }
            : { color: colors.grey[400], label: "Unknown" };
        return (
          <Chip
            label={style.label}
            size="small"
            sx={{
              bgcolor: `${style.color}22`, color: style.color, fontWeight: 600, fontSize: 11, height: 22,
            }}
          />
        );
      },
    },
    {
      field: "service",
      headerName: "SERVICE",
      flex: 0.7,
      minWidth: 110,
      headerAlign: "left",
      align: "left",
      renderCell: (params) => (
        <Typography sx={{ fontSize: 12.5, color: colors.grey[100] }}>
          {params.value || "—"}
        </Typography>
      ),
    },
    {
      field: "port",
      headerName: "PORT / PROTO",
      flex: 0.6,
      minWidth: 110,
      headerAlign: "left",
      align: "left",
      renderCell: (params) => (
        <Typography sx={{ fontFamily: "monospace", fontSize: 12.5, color: colors.grey[100] }}>
          {params.value}{params.row.protocol ? `/${params.row.protocol}` : ""}
        </Typography>
      ),
    },
    {
      field: "lastSeen",
      headerName: "LAST SEEN",
      flex: 0.8,
      minWidth: 150,
      headerAlign: "left",
      align: "left",
      renderCell: (params) => (
        <Typography sx={{ fontFamily: "monospace", fontSize: 12.5, color: colors.grey[300] }}>
          {params.value ? dayjs(params.value).format("YYYY-MM-DD HH:mm:ss") : "—"}
        </Typography>
      ),
    },
  ];

  return (
    <Box sx={{ height: "100%" }}>
      <DataGridPro
        rows={data}
        columns={columns}
        paginationMode="server"
        pageSizeOptions={[25, 50, 100]}
        paginationModel={paginationModel}
        onPaginationModelChange={setPaginationModel}
        rowCount={rowCounter}
        loading={loading}
        getRowHeight={() => "auto"}
        sx={dataGridStyle}
        slotProps={{ loadingOverlay: { variant: "skeleton" } }}
      />
    </Box>
  );
};

export default IpTable;
